import React, { useId } from "react";

/**
 * The Wosool "W" mark — the four strokes and gradients of the landing page's
 * logo (`src/landing/landing.html`, W_PATHS / W_GRADS).
 *
 * `tone="dark"` is for dark grounds (the first stroke is white), `tone="light"`
 * for light ones (the first stroke is navy). Decorative: pair it with a visible
 * name or an aria-label on the link around it.
 */

const PATHS = [
  "M337.2 751.9C330.0 750.7 315.4 745.1 308.3 740.9C300.7 736.5 284.7 720.4 278.8 711.4C264.3 689.2 258.0 671.1 243.5 609.0C232.8 563.5 226.7 544.7 217.8 529.5C212.6 520.7 201.3 509.5 193.4 505.3C182.1 499.3 179.4 499.0 138.8 499.0L102.0 499.0L102.0 449.0L102.0 399.0L132.8 398.8C186.7 398.5 194.1 399.0 215.0 404.6C252.2 414.6 286.5 440.4 305.2 472.3C319.4 496.7 329.6 526.7 345.9 592.5C357.2 637.8 358.8 643.1 365.3 658.4C372.1 674.2 375.9 680.5 383.8 689.1C398.3 704.8 418.5 708.4 435.8 698.3C438.2 697.0 440.4 696.0 440.6 696.3C442.1 697.8 429.0 717.2 420.7 725.8C409.5 737.3 396.9 745.0 381.3 749.6C371.5 752.5 347.6 753.8 337.2 751.9Z",
  "M408.1 680.8C396.8 677.6 386.1 656.7 373.5 613.0C365.0 583.1 365.2 585.6 369.3 570.5C371.3 563.3 378.8 533.9 386.0 505.0C408.4 415.1 407.6 417.4 423.4 400.3C432.4 390.6 438.5 387.6 447.8 388.2C456.7 388.9 461.9 391.6 467.5 398.8C476.9 410.8 485.4 431.7 496.2 469.4L499.3 480.5L495.1 496.5C492.9 505.3 483.8 540.9 475.0 575.6C454.8 655.5 455.2 654.5 441.3 669.1C429.9 681.1 420.4 684.4 408.1 680.8Z",
  "M648.5 751.3C627.0 746.8 606.7 732.1 592.8 710.9C575.6 684.8 569.2 665.1 536.5 539.0C510.1 437.1 508.6 431.8 501.5 415.0C493.5 396.0 486.1 384.8 475.6 375.5C466.5 367.5 461.5 365.6 450.5 365.5C441.6 365.5 441.4 365.6 432.6 370.3C427.7 372.9 423.5 374.8 423.3 374.6C422.4 373.7 428.3 364.2 435.6 354.7C454.7 329.9 478.7 318.6 511.5 318.7C536.4 318.8 555.0 326.4 572.5 343.7C593.1 364.0 606.4 394.2 622.0 456.1C625.4 469.5 632.4 496.2 637.5 515.5C646.0 547.3 657.9 592.9 666.5 626.5C675.0 659.9 687.5 685.7 699.7 695.4C714.4 707.0 729.9 709.9 743.8 703.5C753.6 699.0 754.1 700.1 746.9 710.6C731.0 733.6 710.2 747.4 684.6 751.9C676.2 753.4 656.8 753.1 648.5 751.3Z",
  "M720.2 681.9C712.3 678.0 703.4 664.5 695.7 645.0C692.8 637.6 683.9 605.5 681.5 593.8L679.9 586.0L684.9 564.3C687.7 552.3 693.6 526.3 698.0 506.5C717.2 421.4 722.8 402.4 737.2 373.5C757.0 333.9 788.0 306.4 827.4 293.5C846.9 287.1 859.7 285.6 892.5 285.5L921.5 285.5L921.8 336.2C921.9 364.2 921.9 387.1 921.8 387.2C921.6 387.3 907.8 387.6 891.0 387.9L860.4 388.5L854.0 391.7C828.2 404.5 819.5 425.8 798.0 529.0C775.1 638.3 775.0 638.9 768.4 652.5C761.6 666.4 749.6 679.1 739.7 682.7C734.3 684.6 725.1 684.3 720.2 681.9Z",
];

const FIRST_STROKE = {
  dark: [["#FFFFFF", 0], ["#C9D5EA", 1]],
  light: [["#2B3D61", 0], ["#1F2E4B", 1]],
};

const GRADIENTS = [
  null,
  [["#2C83B0", 0], ["#26B6D0", 0.5], ["#2C84B2", 1]],
  [["#E5306D", 0], ["#EC4C93", 0.45], ["#F06C9C", 0.75], ["#F47B89", 1]],
  [["#F3B141", 0], ["#F19A47", 0.4], ["#EC7C4B", 0.7], ["#E0533A", 1]],
];

function WosoolMark({ tone = "dark", width = 46, className }) {
  // useId's colons are not safe inside url(#…).
  const id = `wm${useId().replace(/:/g, "")}`;
  const grads = GRADIENTS.map((g, i) => (i === 0 ? FIRST_STROKE[tone] || FIRST_STROKE.dark : g));
  return (
    <svg
      viewBox="96 279 832 481"
      width={width}
      height={Math.round((width * 481) / 832)}
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <defs>
        {grads.map((stops, i) => (
          <linearGradient key={i} id={`${id}${i}`} x1="0" y1="0" x2="0" y2="1">
            {stops.map(([color, offset]) => (
              <stop key={offset} offset={offset} stopColor={color} />
            ))}
          </linearGradient>
        ))}
      </defs>
      {PATHS.map((d, i) => (
        <path key={i} fill={`url(#${id}${i})`} d={d} />
      ))}
    </svg>
  );
}

export default WosoolMark;
